/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import { getIn } from 'seamless-immutable';
import { getWarrantyUrl } from '@catalogo/core-helpers/url';
import { withMetrics } from '@catalogo/core-metrics';
import { withConfig } from '@catalogo/core-connect/config';
import withAddToCart from '@catalogo/service-americanas-cart/src/hocs/add-to-cart';
import { IWithAddToCartInjectedProps } from '@catalogo/service-americanas-cart/src/types/add-to-cart';
import { useAddToCartPDP } from '@catalogo/service-americanas-cart/src/hooks/add-to-cart-pdp';
import compose from 'lodash/fp/compose';
import {
  fmtMainProduct,
  fmtExtraProducts,
  toggleSelectedSku,
  fmtBuyTogetherProducts,
  handleClickBuyButton,
} from '../helpers/buy-together';
import { getPayments } from '../payment/payments';
import { useProduct } from '../hooks/product-buy-together';
import { ProductType, WithComboBuyTogetherProps } from '../types';

export const withComboBuyTogether = (Component: React.FC) => ({
  currentSku,
  publication,
  product,
  dispatchMetrics,
  categoryUrl,
  config,
  cartId,
  cartLines,
  addToCart,
  addToCartLoading,
  addToCartError,
  isAddedToCart,
  isUpdatedToCart,
  ...props
}: WithComboBuyTogetherProps & IWithAddToCartInjectedProps) => {
  const [selectedSku, setSelectedSku] = useState<Record<string, { offerId: string }>>({});
  const products = useProduct(publication as never[]);
  const offer = getIn(currentSku, ['offers', 'result', '0']);
  const extra = fmtExtraProducts(products, selectedSku);
  const warrantyUrl = getWarrantyUrl(product.id, currentSku, '', 1, extra);
  const hasServices = offer?.productServices?.length > 0;
  const selectedSkuOffers = Object.values(selectedSku)?.map(sku => ({ offerId: sku?.offerId, quantity: 1 }));

  const { handleAddProduct } = useAddToCartPDP({
    url: warrantyUrl,
    brand: config?.brandNameLower,
    addToCart,
    cartId,
    cartLines,
    isAddedToCart,
    isUpdatedToCart,
    addToCartLoading,
    addToCartError,
    offers: [{ offerId: offer?.id, quantity: 1 }, ...selectedSkuOffers],
    hasServices,
  });

  if (!offer || !products.length || !publication) {
    return null;
  }

  const creditCardPriceWithDiscount = getIn(offer, ['bestPaymentOption', 'minQuantity', '0', 'value']);
  const mainProduct = fmtMainProduct(currentSku, product);
  const { mainPayment } = getPayments(offer);

  const productsFormated = fmtBuyTogetherProducts(
    products,
    selectedSku,
    toggleSelectedSku(selectedSku, setSelectedSku, dispatchMetrics),
    categoryUrl
  );

  const currentProduct = {
    id: product?.id,
    name: product?.name,
    price: mainPayment?.price,
    quantity: 1,
    category: categoryUrl,
    sku: currentSku?.id,
    sellerId: currentSku?.offers?.result?.[0]?.seller?.id,
    sellerName: currentSku?.offers?.result?.[0]?.seller?.name,
    isFromBuyTogether: true,
  };

  const currentSelected =
    productsFormated?.filter((product: ProductType) => selectedSku[product.id as string] && product) ?? [];

  const comboBuyTogetherProps = {
    rating: getIn(mainProduct, ['rating'], null),
    mainProduct,
    selectedSku,
    products: productsFormated,
    title: getIn(publication, ['title']),
    url: getWarrantyUrl(product.id, currentSku, '', 1, extra),
    totalValue: extra.reduce(
      (acc: Record<string, string>, curr: Record<string, string>) => acc + curr.salesPrice,
      creditCardPriceWithDiscount ? creditCardPriceWithDiscount : getIn(offer, ['bestPaymentOption', 'price'])
    ),
    totalProducts: extra.length + 1,
    prime: getIn(offer, ['stamps', 'prime'], false),
    international: getIn(offer, ['currency'], '') === 'USD',
    mainPayment,
    onClickBuyButton: handleClickBuyButton(dispatchMetrics, handleAddProduct, [currentProduct, ...currentSelected]),
  };

  return <Component {...comboBuyTogetherProps} {...props} />;
};

export default compose(withMetrics, withConfig, withAddToCart, withComboBuyTogether);
